// INV35-B1-F1 (docs/build-briefs/INV35-B1-F1-delivery-vendor-picker.md; DEC-300; design/mockups/
// inv35-b1-f1-receive-vendor.html): the optional vendor row and the "Pick a vendor" sheet on the
// "Receive stock (no purchase order)" screen. This module draws and wires them; every ordering and
// wording rule lives in receiveVendorModel.ts, where it is tested without a DOM.
//
// What it guarantees, each one checked by e2e/inv35-b1-f1-receive-vendor.spec.ts:
//   - The vendor is a PICK from the tenant's own list, never typed. The only text field is the
//     sheet's search box, and the list is complete without it.
//   - Nothing is preselected. The item's usual vendor is a one-tap chip, never a default.
//   - The pick carries to the next item of the same visit and goes when the screen is left
//     (reset()) or Clear is tapped.
//   - Turned-off vendors are hidden until the footer chip is tapped, and can still be picked.
//   - Every control is a 44px button or taller, there is no hover rule, no title tooltip and no
//     keyboard or mouse listener: Done, a row, a chip or the dimmed backdrop closes the sheet.
//
// Text is set with textContent only (vendor names are user-entered and one can be 90 characters,
// so names wrap and are never cut).

import type { ReceiptVendorListDto } from "./api.ts";
import {
  LOAD_FAILED_TEXT,
  NO_VENDOR_BOX_TEXT,
  TURNED_OFF_CHOSEN_NOTE,
  TURNED_OFF_ROW_NOTE,
  TURNED_OFF_WARNING,
  barNote,
  footerText,
  sheetView,
  turnedOffChipLabel,
  usedLatelyChips,
  usualVendorChip,
  type VendorOption,
} from "./receiveVendorModel.ts";

export interface ReceiveVendorElements {
  /** Where the vendor box, its chips and its notes are drawn. */
  control: HTMLElement;
  /** Above the product search: names the vendor carried to the next item, with Clear, so a pick
   * is never invisible between two items. */
  carry: HTMLElement;
  backdrop: HTMLElement;
  sheet: HTMLElement;
  done: HTMLButtonElement;
  search: HTMLInputElement;
  searchClear: HTMLButtonElement;
  recent: HTMLElement;
  list: HTMLElement;
  count: HTMLElement;
  toggle: HTMLButtonElement;
  /** The line in the pinned bar beside Record delivery. */
  barNote: HTMLElement;
}

export type ReceiveVendorLoadResult =
  { ok: true; list: ReceiptVendorListDto } | { ok: false; message: string };

export interface ReceiveVendorController {
  /** A fresh visit to the screen: no vendor, closed sheet, nothing loaded. */
  reset(): void;
  /** Read the vendor list (and this item's usual vendor) for the item now being entered. The
   * pick, if any, is kept. */
  loadFor(liquorProductId: string): Promise<void>;
  /** The vendor picked right now, or undefined. */
  chosen(): VendorOption | undefined;
  closeSheet(): void;
  isSheetOpen(): boolean;
}

type LoadState = "idle" | "loading" | "ready" | "failed";

const CHEVRON_PATH = "M2 2l6 6-6 6";
const SVG_NS = "http://www.w3.org/2000/svg";

function chevron(): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("class", "receive-vendor-chevron");
  svg.setAttribute("viewBox", "0 0 10 16");
  svg.setAttribute("aria-hidden", "true");
  const path = document.createElementNS(SVG_NS, "path");
  path.setAttribute("d", CHEVRON_PATH);
  path.setAttribute("fill", "none");
  path.setAttribute("stroke", "currentColor");
  path.setAttribute("stroke-width", "2");
  path.setAttribute("stroke-linecap", "round");
  path.setAttribute("stroke-linejoin", "round");
  svg.append(path);
  return svg;
}

function checkMark(): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("class", "receive-vendor-check");
  svg.setAttribute("viewBox", "0 0 22 22");
  svg.setAttribute("aria-hidden", "true");
  const path = document.createElementNS(SVG_NS, "path");
  path.setAttribute("d", "M4 11.5l4.5 4.5L18 6");
  path.setAttribute("fill", "none");
  path.setAttribute("stroke", "currentColor");
  path.setAttribute("stroke-width", "2.4");
  path.setAttribute("stroke-linecap", "round");
  path.setAttribute("stroke-linejoin", "round");
  svg.append(path);
  return svg;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  testId?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (testId !== undefined) {
    node.dataset["testid"] = testId;
  }
  if (text !== undefined) {
    node.textContent = text;
  }
  return node;
}

function button(
  className: string,
  testId: string,
  text: string,
  onTap: () => void,
): HTMLButtonElement {
  const node = el("button", className, testId, text);
  node.type = "button";
  node.addEventListener("click", onTap);
  return node;
}

export function createReceiveVendorController(deps: {
  elements: ReceiveVendorElements;
  /** Reads GET .../inventory/receipt-vendors for the item. */
  fetchList: (liquorProductId: string) => Promise<ReceiveVendorLoadResult>;
  /** Called after the pick changes (chosen or cleared). */
  onChange?: () => void;
  /** Called as the sheet opens. */
  onOpenSheet?: () => void;
}): ReceiveVendorController {
  const els = deps.elements;
  let vendors: VendorOption[] = [];
  let usedLately: string[] = [];
  let usualVendorId: string | null = null;
  let chosen: VendorOption | undefined;
  let loadState: LoadState = "idle";
  let loadedFor: string | undefined;
  let loadCounter = 0;
  let showTurnedOff = false;
  let query = "";

  function closeSheet(): void {
    els.backdrop.hidden = true;
  }

  function isSheetOpen(): boolean {
    return !els.backdrop.hidden;
  }

  function focusBox(): void {
    els.control.querySelector<HTMLElement>('[data-testid="receive-vendor-open"]')?.focus();
  }

  function pick(vendor: VendorOption | undefined): void {
    chosen = vendor;
    closeSheet();
    render();
    focusBox();
    deps.onChange?.();
  }

  function openSheet(): void {
    query = "";
    els.search.value = "";
    showTurnedOff = chosen !== undefined && !chosen.active;
    els.backdrop.hidden = false;
    deps.onOpenSheet?.();
    renderSheet();
    els.list.scrollTop = 0;
    els.sheet.focus();
  }

  // ---- the row on the entry form ----
  function renderControl(): void {
    els.control.textContent = "";
    if (loadState === "failed") {
      els.control.append(
        el("p", "receive-vendor-load-failed", "receive-vendor-load-failed", LOAD_FAILED_TEXT),
        button("btn-secondary", "receive-vendor-retry", "Try again", () => {
          if (loadedFor !== undefined) {
            void loadFor(loadedFor);
          }
        }),
      );
      return;
    }
    const box = el("button", "receive-vendor-box", "receive-vendor-open");
    box.type = "button";
    box.setAttribute("aria-haspopup", "dialog");
    box.disabled = loadState !== "ready";
    const text = el("span", "receive-vendor-box-text");
    if (chosen === undefined) {
      box.classList.add("is-empty");
      text.textContent = loadState === "loading" ? "Loading vendors" : NO_VENDOR_BOX_TEXT;
      box.append(text, chevron());
      box.setAttribute("aria-label", `Vendor, ${NO_VENDOR_BOX_TEXT}. Opens the vendor list.`);
      box.addEventListener("click", openSheet);
      els.control.append(box);
      const usual = usualVendorChip(usualVendorId, vendors, undefined);
      if (usual !== undefined) {
        const suggest = el("div", "receive-vendor-suggest", "receive-vendor-usual");
        suggest.append(
          el("span", "receive-vendor-suggest-label", undefined, "Usual vendor for this item:"),
          button("receive-vendor-chip", "receive-vendor-usual-chip", usual.name, () => pick(usual)),
        );
        els.control.append(suggest);
      }
      return;
    }
    box.classList.add("is-set");
    const nameBlock = el("span", "receive-vendor-box-text");
    nameBlock.append(
      el("span", "receive-vendor-box-name", "receive-vendor-chosen-name", chosen.name),
    );
    if (!chosen.active) {
      nameBlock.append(el("small", "receive-vendor-box-sub", undefined, TURNED_OFF_CHOSEN_NOTE));
    }
    box.append(nameBlock);
    if (!chosen.active) {
      box.append(
        el("span", "receive-vendor-tag is-warn", "receive-vendor-turned-off-tag", "Turned off"),
      );
    }
    box.append(el("span", "receive-vendor-change", undefined, "Change"));
    box.setAttribute("aria-label", `Vendor: ${chosen.name}. Change vendor.`);
    box.addEventListener("click", openSheet);
    const row = el("div", "receive-vendor-chosen-row");
    row.append(
      box,
      button("btn-secondary receive-vendor-clear", "receive-vendor-clear", "Clear", () =>
        pick(undefined),
      ),
    );
    els.control.append(row);
    if (!chosen.active) {
      els.control.append(
        el(
          "p",
          "receive-vendor-help is-warn",
          "receive-vendor-turned-off-warning",
          TURNED_OFF_WARNING,
        ),
      );
    }
  }

  function renderCarry(): void {
    els.carry.textContent = "";
    els.carry.hidden = chosen === undefined;
    if (chosen === undefined) {
      return;
    }
    const text = el("span", "receive-vendor-carry-text");
    text.append(
      "Vendor for the next item: ",
      el("strong", "receive-vendor-carry-name", "receive-vendor-carry-name", chosen.name),
    );
    if (!chosen.active) {
      text.append(" ", el("span", "receive-vendor-tag is-warn", undefined, "Turned off"));
    }
    els.carry.append(
      text,
      button("btn-secondary", "receive-vendor-carry-clear", "Clear", () => pick(undefined)),
    );
  }

  function renderBar(): void {
    els.barNote.textContent = barNote(chosen);
  }

  function render(): void {
    renderControl();
    renderCarry();
    renderBar();
    if (isSheetOpen()) {
      renderSheet();
    }
  }

  // ---- the sheet ----
  function vendorRow(vendor: VendorOption): HTMLButtonElement {
    const selected = chosen?.id === vendor.id;
    const row = el(
      "button",
      `receive-vendor-row${selected ? " is-selected" : ""}${vendor.active ? "" : " is-off"}`,
      "receive-vendor-row",
    );
    row.type = "button";
    row.dataset["vendorId"] = vendor.id;
    row.setAttribute("aria-pressed", selected ? "true" : "false");
    const name = el("span", "receive-vendor-row-name", undefined, vendor.name);
    if (!vendor.active) {
      name.append(el("small", "receive-vendor-row-sub", undefined, TURNED_OFF_ROW_NOTE));
    }
    row.append(name);
    if (!vendor.active) {
      row.append(el("span", "receive-vendor-tag is-warn", undefined, "Turned off"));
    }
    if (selected) {
      row.append(checkMark());
    }
    row.addEventListener("click", () => pick(vendor));
    return row;
  }

  function renderSheet(): void {
    const view = sheetView(vendors, { showTurnedOff, query });
    els.searchClear.hidden = query.length === 0;

    // Used lately: hidden while searching, like the "No vendor" row.
    els.recent.textContent = "";
    const chips = query.length > 0 ? [] : usedLatelyChips(usedLately, vendors);
    els.recent.hidden = chips.length === 0;
    if (chips.length > 0) {
      els.recent.append(el("span", "receive-vendor-recent-label", undefined, "Used lately"));
      for (const vendor of chips) {
        els.recent.append(
          button("receive-vendor-chip", "receive-vendor-recent-chip", vendor.name, () =>
            pick(vendor),
          ),
        );
      }
    }

    els.list.textContent = "";
    if (query.length === 0) {
      const none = el(
        "button",
        `receive-vendor-row receive-vendor-none${chosen === undefined ? " is-selected" : ""}`,
        "receive-vendor-none",
      );
      none.type = "button";
      none.setAttribute("aria-pressed", chosen === undefined ? "true" : "false");
      const label = el("span", "receive-vendor-row-name", undefined, "No vendor");
      label.append(
        el("small", "receive-vendor-row-sub", undefined, "Leave this delivery without a vendor"),
      );
      none.append(label);
      if (chosen === undefined) {
        none.append(checkMark());
      }
      none.addEventListener("click", () => pick(undefined));
      els.list.append(none);
    }
    for (const group of view.groups) {
      if (group.heading.length > 0) {
        els.list.append(
          el(
            "div",
            `receive-vendor-letter${group.turnedOff ? " is-off" : ""}`,
            group.turnedOff ? "receive-vendor-turned-off-heading" : "receive-vendor-letter",
            group.heading,
          ),
        );
      }
      for (const vendor of group.vendors) {
        els.list.append(vendorRow(vendor));
      }
    }
    if (view.shown === 0) {
      els.list.append(
        el(
          "p",
          "receive-vendor-empty",
          "receive-vendor-empty",
          query.length > 0
            ? view.turnedOff > 0 && !showTurnedOff
              ? "No vendor matches. Turned off vendors are hidden."
              : "No vendor matches."
            : "There are no vendors yet. Add one in Settings.",
        ),
      );
    }

    els.count.textContent = footerText(view, showTurnedOff);
    const chipLabel = turnedOffChipLabel(view, showTurnedOff);
    els.toggle.hidden = chipLabel === null;
    els.toggle.textContent = chipLabel ?? "";
    els.toggle.classList.toggle("is-on", showTurnedOff);
    els.toggle.setAttribute("aria-pressed", showTurnedOff ? "true" : "false");
  }

  // ---- wiring (once) ----
  els.done.addEventListener("click", () => {
    closeSheet();
    focusBox();
  });
  els.backdrop.addEventListener("click", (event) => {
    if (event.target === els.backdrop) {
      closeSheet();
      focusBox();
    }
  });
  els.search.addEventListener("input", () => {
    query = els.search.value;
    renderSheet();
    els.list.scrollTop = 0;
  });
  els.searchClear.addEventListener("click", () => {
    query = "";
    els.search.value = "";
    renderSheet();
    els.search.focus();
  });
  els.toggle.addEventListener("click", () => {
    showTurnedOff = !showTurnedOff;
    renderSheet();
    if (showTurnedOff) {
      els.list.scrollTop = els.list.scrollHeight;
    }
  });

  async function loadFor(liquorProductId: string): Promise<void> {
    loadedFor = liquorProductId;
    const mine = ++loadCounter;
    loadState = "loading";
    render();
    const result = await deps.fetchList(liquorProductId);
    if (mine !== loadCounter) {
      return; // a newer item replaced this read
    }
    if (!result.ok) {
      loadState = "failed";
      render();
      return;
    }
    vendors = result.list.vendors;
    usedLately = result.list.usedLately;
    usualVendorId = result.list.usualVendorId;
    // Refresh the carried pick from the new list (its name or turned-off state may have changed).
    // A vendor the list no longer holds is kept as it was picked: the pick was real.
    if (chosen !== undefined) {
      const fresh = vendors.find((v) => v.id === chosen?.id);
      if (fresh !== undefined) {
        chosen = fresh;
      }
    }
    loadState = "ready";
    render();
  }

  function reset(): void {
    loadCounter += 1; // drop any read still in flight
    vendors = [];
    usedLately = [];
    usualVendorId = null;
    chosen = undefined;
    loadState = "idle";
    loadedFor = undefined;
    showTurnedOff = false;
    query = "";
    els.search.value = "";
    closeSheet();
    render();
  }

  reset();
  return { reset, loadFor, chosen: () => chosen, closeSheet, isSheetOpen };
}
